import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  FileSpreadsheet, Download, Calendar, CalendarDays,
  Target, CreditCard, FileText, IndianRupee, Loader2, Clock
} from 'lucide-react';

const TIME_RANGES = [
  { id: 'today', label: 'Today', icon: Clock, desc: 'Last 24 hours' },
  { id: 'weekly', label: 'Weekly', icon: Calendar, desc: 'Last 7 days' },
  { id: 'monthly', label: 'Monthly', icon: CalendarDays, desc: 'This month' },
  { id: 'yearly', label: 'Yearly', icon: CalendarDays, desc: 'This year' },
];

const DATA_TYPES = [
  { id: 'leads', label: 'Leads / Inquiries', icon: Target, color: 'text-blue-500', desc: 'All leads with client details, stage, priority' },
  { id: 'bookings', label: 'Bookings', icon: FileText, color: 'text-emerald-500', desc: 'Confirmed leads with closure details' },
  { id: 'quotations', label: 'Quotations', icon: FileSpreadsheet, color: 'text-violet-500', desc: 'Pricing quotations with line items' },
  { id: 'revenue', label: 'Revenue Data', icon: IndianRupee, color: 'text-voyage-orange', desc: 'Payments received with method and status' },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [rangeType, setRangeType] = useState('monthly');
  const [selectedTypes, setSelectedTypes] = useState(['leads']);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  const toggleType = (typeId) => {
    setSelectedTypes(prev =>
      prev.includes(typeId) ? prev.filter(t => t !== typeId) : [...prev, typeId]
    );
  };

  const handleDownload = async () => {
    if (selectedTypes.length === 0) {
      toast.error('Select at least one data type to export');
      return;
    }
    if (showCustom && (!customStart || !customEnd)) {
      toast.error('Select both start and end dates for custom range');
      return;
    }

    setDownloading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const range = showCustom ? 'custom' : rangeType;
      let url = `${backendUrl}/api/reports/export?data_types=${selectedTypes.join(',')}&range_type=${range}`;
      if (showCustom) {
        url += `&start_date=${new Date(customStart).toISOString()}&end_date=${new Date(customEnd).toISOString()}`;
      }

      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Export failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'VoyageOS_Report.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast.success('Report downloaded successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to export report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">
          Reports & Export
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Export your business data in seconds — daily, weekly, monthly, or yearly.
        </p>
      </div>

      {/* Time Range Selection */}
      <Card className="border border-border bg-card" data-testid="time-range-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-voyage-blue" /> Select Time Range
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {TIME_RANGES.map(range => {
              const isActive = rangeType === range.id && !showCustom;
              return (
                <button
                  key={range.id}
                  onClick={() => { setRangeType(range.id); setShowCustom(false); }}
                  className={`p-3 rounded-xl border-2 text-left transition-all duration-200 card-hover-lift ${
                    isActive
                      ? 'border-voyage-blue bg-voyage-blue/5 shadow-md shadow-voyage-blue/10'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                  data-testid={`range-${range.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <range.icon className={`w-4 h-4 ${isActive ? 'text-voyage-blue' : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-semibold ${isActive ? 'text-voyage-blue' : 'text-foreground'}`}>{range.label}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{range.desc}</p>
                </button>
              );
            })}
          </div>

          {/* Custom Date Range Toggle */}
          <div>
            <button
              onClick={() => setShowCustom(!showCustom)}
              className={`text-xs font-medium transition-colors ${showCustom ? 'text-voyage-blue' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="custom-range-toggle"
            >
              {showCustom ? 'Using custom date range' : 'Or use custom date range'}
            </button>
            {showCustom && (
              <div className="flex gap-3 mt-3 animate-fade-in">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Start Date</Label>
                  <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                    className="h-9 text-sm" data-testid="custom-start-date" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                    className="h-9 text-sm" data-testid="custom-end-date" />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Type Selection */}
      <Card className="border border-border bg-card" data-testid="data-types-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-voyage-orange" /> Select Data to Export
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DATA_TYPES.map(dt => {
              const isChecked = selectedTypes.includes(dt.id);
              return (
                <label
                  key={dt.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 card-hover-lift ${
                    isChecked
                      ? 'border-voyage-blue bg-voyage-blue/5'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                  data-testid={`dtype-${dt.id}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleType(dt.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <dt.icon className={`w-4 h-4 flex-shrink-0 ${dt.color}`} />
                      <span className="text-sm font-semibold text-foreground">{dt.label}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{dt.desc}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Download Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <Button
          onClick={handleDownload}
          disabled={downloading || selectedTypes.length === 0}
          className="gap-2.5 h-12 px-8 gradient-blue hover:opacity-90 text-white shadow-lg shadow-voyage-blue/20 text-sm font-semibold"
          data-testid="download-excel-btn"
        >
          {downloading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Generating Report...</>
          ) : (
            <><Download className="w-4 h-4" /> Download Excel</>
          )}
        </Button>
        <div className="text-xs text-muted-foreground space-y-0.5">
          <p className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono">.xlsx</Badge>
            Excel format with formatted headers and auto-width columns
          </p>
          {selectedTypes.length > 0 && (
            <p>
              Exporting: {selectedTypes.map(t => DATA_TYPES.find(d => d.id === t)?.label).join(', ')} — {showCustom ? 'Custom range' : TIME_RANGES.find(r => r.id === rangeType)?.label}
            </p>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
        <CreditCard className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p>Each export generates a professionally formatted Excel file with separate sheets per data type. Multiple selections will create multiple sheets in a single file.</p>
          <p className="mt-1">File naming: <span className="font-mono text-xs">VoyageOS_Report_Type_DateRange.xlsx</span></p>
        </div>
      </div>
    </div>
  );
}
